import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Camera, ChefHat } from "lucide-react";

const DIETS = [
  { key: "any", label: "Anything" },
  { key: "veg", label: "Vegetarian" },
  { key: "vegan", label: "Vegan" },
];
const EFFORTS = [
  { key: "quick", label: "Quick", note: "≤ 20 min, minimal faff" },
  { key: "proper", label: "Proper", note: "a real cook" },
];
const CUISINES = [
  { key: "any", label: "Surprise me" },
  { key: "italian", label: "Italian-ish" },
  { key: "comfort", label: "Comfort" },
  { key: "spicy", label: "Spicy" },
  { key: "fresh", label: "Fresh" },
  { key: "asian", label: "Asian-ish" },
];

export default function Cook() {
  const navigate = useNavigate();
  const [diet, setDiet] = useState("any");
  const [effort, setEffort] = useState("quick");
  const [cuisine, setCuisine] = useState("any");

  return (
    <div className="app-shell min-h-[100dvh] px-6 pt-[calc(env(safe-area-inset-top)+1.5rem)] pb-[calc(env(safe-area-inset-bottom)+3rem)]" data-testid="cook-screen">
      <div className="flex items-center justify-between mb-8">
        <button data-testid="cook-back-btn" onClick={() => navigate("/")} className="w-9 h-9 rounded-full glass flex items-center justify-center">
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
        </button>
        <span className="text-xs uppercase tracking-[0.28em] font-semibold text-[#4a5c62]">The kitchen</span>
        <div className="w-9" />
      </div>

      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}>
        <div className="inline-flex items-center gap-2 glass rounded-full px-3 py-1.5 mb-5">
          <ChefHat className="w-4 h-4 text-[#4a5c62]" strokeWidth={1.5} />
          <span className="text-xs uppercase tracking-[0.18em] font-semibold text-[#4a5c62]">Cook mode</span>
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl font-black tracking-tighter leading-[0.95]">
          What can I
          <br />
          cook?
        </h1>
        <p className="text-base text-[#4a5c62] mt-4">Snap the fridge. We'll spot what's in there and suggest three things worth making.</p>
      </motion.div>

      <div className="mt-8">
        <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#4a5c62] mb-3">Diet</p>
        <div className="flex gap-2">
          {DIETS.map((d) => (
            <button
              key={d.key}
              data-testid={`diet-${d.key}-btn`}
              onClick={() => setDiet(d.key)}
              className={`flex-1 rounded-sm py-3 text-sm font-medium transition-colors ${diet === d.key ? "bg-[#16242a] text-[#eef5f6]" : "glass text-[#4a5c62]"}`}
            >
              {d.label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-6">
        <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#4a5c62] mb-3">Effort</p>
        <div className="flex gap-2">
          {EFFORTS.map((e) => (
            <button
              key={e.key}
              data-testid={`effort-${e.key}-btn`}
              onClick={() => setEffort(e.key)}
              className={`flex-1 rounded-sm py-3 text-left px-4 transition-colors ${effort === e.key ? "bg-[#16242a] text-[#eef5f6]" : "glass text-[#4a5c62]"}`}
            >
              <span className="block text-sm font-semibold">{e.label}</span>
              <span className="block text-[11px] opacity-70">{e.note}</span>
            </button>
          ))}
        </div>
      </div>

      <div className="mt-6">
        <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#4a5c62] mb-3">Mood</p>
        <div className="flex flex-wrap gap-2">
          {CUISINES.map((c) => (
            <button
              key={c.key}
              data-testid={`cuisine-${c.key}-btn`}
              onClick={() => setCuisine(c.key)}
              className={`rounded-full px-4 py-2.5 text-sm font-medium transition-colors ${cuisine === c.key ? "bg-[#16242a] text-[#eef5f6]" : "glass text-[#4a5c62]"}`}
            >
              {c.label}
            </button>
          ))}
        </div>
      </div>

      <button
        data-testid="cook-snap-btn"
        onClick={() => navigate(`/capture?mode=cook&diet=${diet}&effort=${effort}&cuisine=${cuisine}`)}
        className="btn-ink w-full rounded-sm py-5 mt-10 flex items-center justify-between text-left"
      >
        <span>
          <span className="block font-serif text-2xl">Snap the fridge</span>
          <span className="block text-sm opacity-70 mt-1">One photo · then three ideas</span>
        </span>
        <Camera className="w-6 h-6 shrink-0" strokeWidth={1.5} />
      </button>
    </div>
  );
}
